import { Observable, of } from 'rxjs';
import {
  CustomFieldProps,
  DynamicField,
  DynamicForm,
  EditorFieldProps,
  FieldVariant,
  FileFieldProps,
  SelectFieldProps,
  SelectOption,
  TextFieldProps,
  DateFieldProps,
} from '../../../../components/form-builder/form-builder.types';
import { BudgetEstimateComponent } from '../../../../components/shared/budget-estimate/budget-estimate.component';
import { MultiFormAccordionComponent } from '../../../../components/multi-form-accordion/multi-form-accordion.component';
import { PaaRepository } from '../../../../store/paa-state/paa-state.repository';
import { FileRemoveEvent, FileSelectEvent } from 'primeng/fileupload';
import { PaaMilestoneFormComponent } from '../../milestone-form/milestone-form.component';

interface YearFieldConfig {
  minDate?: Date;
  maxDate?: Date;
  allowedYears?: number[];
  isDisabled?: Observable<boolean>;
}

interface getPaaNewPlanFormObjectProps {
  store: PaaRepository;
  acOptions: SelectOption[];
  yearConfig?: YearFieldConfig;
  onAcChange?: (acId?: number) => void;
}

export const getPaaNewPlanFormObject = ({ store, acOptions, yearConfig, onAcChange }: getPaaNewPlanFormObjectProps): DynamicForm => {
  const acOptionsField: DynamicField<SelectFieldProps> = {
    label: 'paa.form.ac_options',
    variant: FieldVariant.SELECT,
    isRequired: true,
    isDisabled: of(acOptions.length === 1),
    error: store.getNestedObservable<string>('errors', 'acId'),
    props: {
      value: store.getObservable('acsSelected'),
      onSelectChange: (value: SelectOption) => {
        store.set('acsSelected', value);
        store.setNested('dto', 'acId', value?.code as number);
        store.clearError('acId');
        store.clearError('annee');
        onAcChange?.(value?.code as number);
      },
      placeholder: 'paa.form.placeholders.ac_options',
      options: acOptions,
      filterEnabled: true,
    },
  };

  const yearField: DynamicField<DateFieldProps> = {
    label: 'paa.form.year',
    variant: FieldVariant.DATE,
    isRequired: true,
    isDisabled: yearConfig?.isDisabled,
    error: store.getNestedObservable<string>('errors', 'annee'),
    props: {
      value: store.getNestedObservable<number>('dto', 'annee'),
      onChange: (value: Date) => {
        if (!value) {
          store.setNested('dto', 'annee', undefined);
          return;
        }
        const year = value.getFullYear();
        if (yearConfig?.allowedYears?.length && !yearConfig.allowedYears.includes(year)) return;
        store.setNested('dto', 'annee', year.toString());
        store.clearError('annee');
      },
      view: 'year',
      format: 'yy',
      minDate: yearConfig?.minDate,
      lastDate: yearConfig?.maxDate,
    },
  };

  const nomField: DynamicField<TextFieldProps> = {
    label: 'paa.form.name',
    variant: FieldVariant.TEXT,
    isRequired: true,
    error: store.getNestedObservable('errors', 'nom'),
    props: {
      value: store.getNestedObservable<string>('dto', 'nom'),
      onChange: (value: string) => {
        store.setNested('dto', 'nom', value);
        store.clearError('nom');
      },
      placeholder: 'paa.form.placeholders.name',
    },
  };

  const milestoneField: DynamicField<CustomFieldProps> = {
    label: 'paa.form.milestone',
    variant: FieldVariant.CUSTOM,
    isRequired: true,
    props: {
      component: MultiFormAccordionComponent,
      injectKeys: ['accordionObject'],
      accordionObject: {
        titles: ['paa.form.milestone2'],
        component: PaaMilestoneFormComponent,
        injectKeys: ['acId'],
        acId: store.getNestedObservable<number>('dto', 'acId'),
        downSyndromeComponent: BudgetEstimateComponent,
        downSyndromeProps: {
          label: 'milestone.form.estimated_total_budget',
          value: store.getObservable<number>('totalEstimatedBudget'),
        },
      },
    },
  };

  const noteField: DynamicField<EditorFieldProps> = {
    label: 'Remarque',
    variant: FieldVariant.EDITOR,
    props: {
      height: '320px',
      value: store.getNestedObservable<string>('dto', 'remarque'),
      onChange: (value: string) => {
        store.setNested('dto', 'remarque', value);
        store.clearError('remarque');
      },
    },
  };

  const uploadField: DynamicField<FileFieldProps> = {
    label: 'Pièce jointe',
    variant: FieldVariant.FILE,
    props: {
      mode: 'advanced',
      accept: 'image/*, application/pdf',
      multiple: true,
      value: store.getNestedObservable<File[]>('dto', 'paaFiles'),
      onFileSelect: (event: FileSelectEvent) => {
        store.setNested('dto', 'paaFiles', event.currentFiles);
      },
      onFileRemoved: (event: FileRemoveEvent) => {
        store.setNested(
          'dto',
          'paaFiles',
          store.getNested<File[]>('dto', 'paaFiles').filter(file => file.name !== event.file.name)
        );
      },
    },
  };

  return {
    title: 'Plan annuel des achats de cette année',
    description: 'Gérez et suivez votre plan annuel des achats pour cette année, Modifiez, mettez  à jour ou visualisez.',
    orientation: 'horizontal',
    isHeaderHidden: true,
    grids: [
      {
        title: 'Information Avis',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [acOptionsField],
          },
          {
            fields: [yearField, nomField],
          },
        ],
      },
      {
        title: 'Réalisation envisagée',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [milestoneField],
          },
        ],
      },
      {
        title: 'Entrée secondaire',
        gridItems: [
          {
            fields: [uploadField],
          },
          {
            fields: [noteField],
          },
        ],
      },
    ],
  };
};
